'use client';

import { useTranslations } from 'next-intl';
import { type ChangeEvent, useCallback, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy, useGlobalModal } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import type { useClawFolder } from '../../../_hook/use-claw-folder';
import { useClawInactiveHandler } from '../../../_hook/use-claw-inactive-handler';
import { useUploadWithRenameOnConflict } from '../../../_hook/use-upload-with-rename-on-conflict';
import { isAllowedUploadFile, normalizePath, openDownloadUrl } from '../../../_utils';
import { getDownloadUrl } from '../../../apis';
import type { ClawFileItem } from '../../../types';
import { isFolder } from '../utils';

const WATERMARK_POLL_INTERVAL = 10000;
const WATERMARK_POLL_MAX = 100;

type ClawFolderApi = ReturnType<typeof useClawFolder>;

/**
 * 三种读侧（Grid/Tree/FilePage）对写操作后的刷新策略不同，由调用方传入：
 * - Grid：通常只调用 `bumpClawFileListRefreshKey()`，让整个 session 刷一次
 * - Tree/FilePage：按路径粒度调用 `refreshFolder` / `refreshFolders`
 */
interface ClawFileOpRefreshStrategy {
  afterCreateFolder: (targetFolderPath: string) => void | Promise<void>;
  afterUploadFiles: (targetFolderPath: string) => void | Promise<void>;
  afterDelete: (item: ClawFileItem) => void | Promise<void>;
}

interface UseClawFileOperationsOptions {
  activeClawId?: string;
  clawFolderApi: ClawFolderApi;
  refreshStrategy: ClawFileOpRefreshStrategy;
  /**
   * 打开创建/上传面板或右键操作未指定目标路径时，hook 会回退到此函数给出的默认目标路径。
   */
  getDefaultTargetFolderPath?: () => string;
}

export const useClawFileOperations = ({
  activeClawId,
  clawFolderApi,
  refreshStrategy,
  getDefaultTargetFolderPath,
}: UseClawFileOperationsOptions) => {
  const t = useTranslations();
  const { copy } = useCopy();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { handleClawInactiveError } = useClawInactiveHandler();
  const { uploadWithRenameOnConflict } = useUploadWithRenameOnConflict();

  const {
    creatingFolder,
    uploadingFile,
    deletingPath,
    createFolder,
    deleteEntry,
    getPreviewUrl,
    uploadFiles: uploadFilesBatch,
  } = clawFolderApi;

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [createFolderTargetPath, setCreateFolderTargetPath] = useState('');
  const [uploadTargetFolderPath, setUploadTargetFolderPath] = useState('');
  const [watermarkDownloadingPath, setWatermarkDownloadingPath] = useState<string | null>(null);

  const resolveTargetFolderPath = useCallback(
    (explicit?: string): string => {
      const normalized = normalizePath(explicit);
      if (normalized) return normalized;
      return getDefaultTargetFolderPath?.() ?? '';
    },
    [getDefaultTargetFolderPath],
  );

  const openCreateFolderDialog = useCallback(
    (targetFolderPath?: string) => {
      reportEvent('new_folder_click');
      setCreateFolderTargetPath(resolveTargetFolderPath(targetFolderPath));
      setIsCreateFolderOpen(true);
    },
    [resolveTargetFolderPath],
  );

  const handleCreateFolderDialogOpenChange = useCallback((open: boolean) => {
    setIsCreateFolderOpen(open);
    if (!open) {
      setCreateFolderTargetPath('');
    }
  }, []);

  const handleCreateFolder = useCallback(async () => {
    const trimmedName = folderName.trim();
    if (!trimmedName || creatingFolder) return;

    const targetFolderPath = createFolderTargetPath;
    const { data, error } = await createFolder({ name: trimmedName, parentPath: targetFolderPath });

    if (error || !data) {
      if (handleClawInactiveError(error)) {
        setIsCreateFolderOpen(false);
        setCreateFolderTargetPath('');
        return;
      }
      const reason =
        error && typeof error === 'object' && 'reason' in error ? (error as { reason?: string }).reason : undefined;
      toast({
        type: 'error',
        message: reason === 'Conflict' ? t('claw.file.nameConflict') : t('claw.file.createFolderFailed'),
      });
      return;
    }

    setFolderName('');
    setIsCreateFolderOpen(false);
    setCreateFolderTargetPath('');

    await refreshStrategy.afterCreateFolder(targetFolderPath);

    toast({ type: 'success', message: t('claw.file.createFolderSuccess') });
  }, [createFolder, createFolderTargetPath, creatingFolder, folderName, refreshStrategy, handleClawInactiveError]);

  const triggerUpload = useCallback(
    (targetFolderPath?: string) => {
      reportEvent('upload_new_folder_click');
      setUploadTargetFolderPath(resolveTargetFolderPath(targetFolderPath));
      fileInputRef.current?.click();
    },
    [resolveTargetFolderPath],
  );

  // 上传核心：input 选择与拖拽 drop 共用同一套带改名兜底的批量上传 + 刷新逻辑。
  const runUpload = useCallback(
    async (files: File[], targetFolderPath: string) => {
      if (!files.length || uploadingFile || !activeClawId) return;

      const { successCount, clawInactive } = await uploadWithRenameOnConflict({
        files,
        targetFolderPath,
        isAllowedFile: isAllowedUploadFile,
        // 复用 useClawFolder.uploadFiles 单文件版本：保留其内部的 uploadingFile 状态切换，
        // 让文件页 Upload 按钮的 loading 表现保持现有行为。
        uploadOnce: async (file) => {
          const result = await uploadFilesBatch({
            files: [file],
            targetFolderPath,
          });
          return {
            ok: result.successCount === 1,
            conflict: result.conflictFileNameList.includes(file.name),
            invalidName: result.invalidFileNameList.includes(file.name),
            clawInactive: result.clawInactive,
            moderateRejected: result.moderateRejectedFileNameList.includes(file.name),
          };
        },
      });

      // claw 已失效：inactive handler 已由 hook 统一触发，这里无需刷新。
      if (clawInactive) return;
      if (successCount > 0) {
        await refreshStrategy.afterUploadFiles(targetFolderPath);
      }
    },
    [activeClawId, refreshStrategy, uploadWithRenameOnConflict, uploadFilesBatch, uploadingFile],
  );

  const handleUploadInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const selectedFiles = Array.from(input.files ?? []);
      const targetFolderPath = uploadTargetFolderPath || resolveTargetFolderPath();
      await runUpload(selectedFiles, targetFolderPath);
      input.value = '';
      setUploadTargetFolderPath('');
    },
    [resolveTargetFolderPath, runUpload, uploadTargetFolderPath],
  );

  // 拖拽 drop 上传：目标目录走默认目录（当前所在/选中目录），与点击上传一致。
  const handleDropFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      await runUpload(files, resolveTargetFolderPath());
    },
    [resolveTargetFolderPath, runUpload],
  );

  const handleDelete = useCallback(
    async (item: ClawFileItem) => {
      const itemPath = normalizePath(item.path);
      if (!itemPath || !activeClawId || deletingPath) return;

      const type = isFolder(item) ? 'folder' : 'file';
      const { error } = await deleteEntry({ path: itemPath, type });

      if (error) {
        toast({ type: 'error', message: t('claw.file.deleteFailed') });
        return;
      }

      await refreshStrategy.afterDelete(item);
      toast({ type: 'success', message: t('claw.file.deleteSuccess') });
    },
    [activeClawId, deleteEntry, deletingPath, refreshStrategy],
  );

  const deleteWithConfirm = useCallback(
    (item: ClawFileItem) => {
      if (deletingPath) return;
      openConfirmDialog({
        inheritTheme: true,
        content: t('claw.file.deleteConfirm'),
        secondaryButtonText: t('common.dialog.cancel'),
        primaryButtonText: t('common.dialog.confirm'),
        onPrimaryAction: async () => {
          await handleDelete(item);
        },
      });
    },
    [deletingPath],
  );

  const handleDownloadPath = useCallback(
    async (path: string) => {
      const filePath = normalizePath(path);
      if (!filePath) return;
      const downloadUrl = await getPreviewUrl(filePath);
      if (!downloadUrl) {
        toast({ type: 'error', message: t('claw.file.downloadFailed') });
        return;
      }
      openDownloadUrl(downloadUrl);
    },
    [getPreviewUrl],
  );

  const handleDownload = useCallback((item: ClawFileItem) => handleDownloadPath(item.path ?? ''), [handleDownloadPath]);

  const handleWatermarkDownload = useCallback(
    async (path: string) => {
      if (!activeClawId) return;
      const filePath = normalizePath(path);
      if (!filePath || watermarkDownloadingPath === filePath) return;

      setWatermarkDownloadingPath(filePath);
      try {
        const { data: initData, error: initError } = await getDownloadUrl({
          query: { claw_id: activeClawId, file_path: filePath, watermark: true },
        });
        if (initError || !initData) {
          toast({ type: 'error', message: t('claw.file.downloadFailed') });
          return;
        }
        if (initData.download_url) {
          openDownloadUrl(initData.download_url);
          return;
        }
        if (initData.watermark_status === 'FAILED') {
          toast({ type: 'error', message: t('claw.file.downloadFailed') });
          return;
        }

        let taskId = initData.watermark_task_id;
        for (let i = 0; i < WATERMARK_POLL_MAX; i++) {
          await new Promise<void>((resolve) => setTimeout(resolve, WATERMARK_POLL_INTERVAL));
          const { data, error } = await getDownloadUrl({
            query: { claw_id: activeClawId, file_path: filePath, watermark: true, watermark_task_id: taskId },
          });
          if (error || !data) {
            toast({ type: 'error', message: t('claw.file.downloadFailed') });
            return;
          }
          if (data.download_url) {
            openDownloadUrl(data.download_url);
            return;
          }
          if (data.watermark_status === 'FAILED') {
            toast({ type: 'error', message: t('claw.file.downloadFailed') });
            return;
          }
          taskId = data.watermark_task_id;
        }
        // 超时
        toast({ type: 'error', message: t('claw.file.downloadFailed') });
      } finally {
        setWatermarkDownloadingPath(null);
      }
    },
    [activeClawId, watermarkDownloadingPath, t],
  );

  const handleWatermarkDownloadItem = useCallback(
    (item: ClawFileItem) => handleWatermarkDownload(item.path ?? ''),
    [handleWatermarkDownload],
  );

  const handleCopyItemPath = useCallback(
    (item: ClawFileItem) => {
      const itemPath = normalizePath(item.path);
      if (!itemPath) return;
      copy(itemPath, {
        copySuccessMessage: t('claw.file.copySuccess'),
        copyFailedMessage: t('claw.file.copyFailed'),
      }).catch(() => undefined);
    },
    [copy],
  );

  const handleCopyPath = useCallback(
    (path: string) => {
      const normalized = normalizePath(path);
      if (!normalized) return;
      copy(normalized, {
        copySuccessMessage: t('claw.file.copySuccess'),
        copyFailedMessage: t('claw.file.copyFailed'),
      }).catch(() => undefined);
    },
    [copy],
  );

  return {
    creatingFolder,
    uploadingFile,
    deletingPath,
    watermarkDownloadingPath,
    fileInputRef,
    createFolderDialog: {
      open: isCreateFolderOpen,
      folderName,
      creatingFolder,
      setOpen: handleCreateFolderDialogOpenChange,
      setFolderName,
      handleCreateFolder,
      openFor: openCreateFolderDialog,
    },
    triggerUpload,
    handleUploadInputChange,
    handleDropFiles,
    handleDelete,
    deleteWithConfirm,
    handleDownload,
    handleDownloadPath,
    handleWatermarkDownload,
    handleWatermarkDownloadItem,
    handleCopyItemPath,
    handleCopyPath,
  };
};
